import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import { CommonService } from '../common.service';
import { ThemeService } from '../theme';
import { IAccountDeleteData, IAuthResponseAPI, IChangePasswordData, IForgotPasswordData, ILoginData, IRegisterData, IResetPasswordData, IResponseMessage, IUser, IUserModulePermission, IVerficationCodeData } from '../../models';
import { APIConstants, Constants } from '../../common';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  isSignInLoading = false;

  constructor(
    private router: Router,
    private http: HttpClient,
    private commonService: CommonService,
    private themeService: ThemeService
  ) { }

  login(loginData: ILoginData) {
    this.isSignInLoading = true;
    this.authenticate(loginData).subscribe({
      next: (data: IAuthResponseAPI) => {
        this.isSignInLoading = false;
        const token = data?.data?.token ? data.data.token : null;
        if (token) {
          localStorage.setItem(Constants.localStorageItem.token, token);
          this.router.navigate([Constants.routingPaths.dashboard]);
        }
        else {
          this.commonService.openDialog({
            type: 'error',
            title: 'Error',
            subtitle: Constants.notifyMessages.loginFailed
          });
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isSignInLoading = false;
        console.log(error);
        this.commonService.openDialog({
          type: 'error',
          title: 'Error',
          subtitle: error?.error?.message ? error.error.message : Constants.notifyMessages.loginFailed
        });
      }
    })
  }

  loginWithGoogle(credential: string) {
    this.commonService.isLoading = true;
    this.googleAuthenticate(credential).subscribe({
      next: (data: IAuthResponseAPI) => {
        // console.log(data);
        this.commonService.isLoading = false;
        const token = data?.data?.token ? data.data.token : null;
        if (token) {
          localStorage.setItem(Constants.localStorageItem.token, token);
          this.router.navigate([Constants.routingPaths.dashboard]);
        }
        else {
          this.commonService.openDialog({
            type: 'error',
            title: 'Error',
            subtitle: Constants.notifyMessages.loginFailed
          });
        }
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
        this.commonService.isLoading = false;
        this.commonService.openDialog({
          type: 'error',
          title: 'Error',
          subtitle: error?.error?.message ? error.error.message : Constants.notifyMessages.loginFailed
        });
      }
    })
  }

  logout() {
    this.clearToken().subscribe({
      next: () => {
        this.completeLogout();
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
        this.completeLogout();
      }
    })
  }

  private completeLogout(): void {
    localStorage.removeItem(Constants.localStorageItem.token);
    this.commonService.profileDetail = {} as IUser;
    this.commonService.currentUserProfile$.next(null);
    this.commonService.userPermission = [];
    this.commonService.isPermissionLoaded = false;
    this.themeService.applyDefaultTheme();
    this.router.navigate([Constants.routingPaths.login]);
  }

  isLoggedIn() {
    const token = localStorage.getItem(Constants.localStorageItem.token);
    return token ? true : false;
  }

  canActivate(): boolean {
    const token = localStorage.getItem(Constants.localStorageItem.token);
    if (token) {
      return true;
    }
    else {
      this.router.navigate([Constants.routingPaths.login]);
      return false;
    }
  }

  canActivateLogin(): boolean {
    const token = localStorage.getItem(Constants.localStorageItem.token);
    if (!token) {
      return true;
    }
    else {
      this.router.navigate([Constants.routingPaths.dashboard]);
      return false;
    }
  }

  canActivatePermission(route: ActivatedRouteSnapshot): Promise<boolean> | boolean {
    if (!this.commonService.isPermissionLoaded) {
      return this.loadPermissions(route);
    }
    return this.checkPermissions(route);
  }

  canActivateLauncher(): boolean | Observable<boolean> {
    if (this.commonService.currentUserProfile$.value) {
      return this.checkLauncherAvailability();
    }

    return this.commonService.getProfileDetails().pipe(
      map(() => this.checkLauncherAvailability()),
      catchError(() => {
        this.router.navigate([Constants.routingPaths.dashboard]);
        return of(false);
      })
    );
  }

  private checkLauncherAvailability(): boolean {
    if (this.commonService.isLauncherAvailable()) return true;

    this.router.navigate([Constants.routingPaths.dashboard]);
    return false;
  }

  private async loadPermissions(route: ActivatedRouteSnapshot): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.commonService.getCurrentUserPermission().subscribe({
        next: (permissionData: IUserModulePermission) => {
          this.commonService.userPermission = permissionData?.permissions?.length > 0 ? permissionData.permissions : [];
          this.commonService.profileDetail = this.commonService.profileDetail ? this.commonService.profileDetail : {} as IUser;
          this.commonService.profileDetail.is_admin = permissionData?.is_admin;
          this.commonService.isPermissionLoaded = true;
          resolve(this.checkPermissions(route));
        },
        error: () => {
          this.router.navigate([Constants.routingPaths.accessDenied]);
          reject(false);
        }
      });
    });
  }

  checkPermissions(route: ActivatedRouteSnapshot) {
    const firstSegment = route.url[0]?.path;
    const path_name = firstSegment ? firstSegment.replace(/\s+/g, '_').toLocaleLowerCase() : '';
    const screen_name = Constants.permissionRoutingPathGroups.find(x => x.path === path_name);
    const screen_permission = this.commonService.userPermission?.find(x => x.module_name.replace(/\s+/g, '_').toLocaleLowerCase() == screen_name?.path?.toLowerCase());
    if (screen_permission?.is_enabled == true || this.commonService.profileDetail.is_admin) {
      return true;
    }
    else {
      this.router.navigate([Constants.routingPaths.accessDenied]);
      return false;
    }
  }

  //API CALL
  authenticate(loginData: ILoginData): Observable<IAuthResponseAPI> {
    return this.http.post<IAuthResponseAPI>(APIConstants.tokenAuth, loginData);
  }

  googleAuthenticate(credential: string) {
    return this.http.post<IAuthResponseAPI>(APIConstants.googleAuth, { 'credential': credential });
  }

  clearToken(): Observable<IResponseMessage> {
    return this.http.get<IResponseMessage>(APIConstants.clearToken);
  }

  register(registerData: IRegisterData): Observable<IResponseMessage> {
    return this.http.post<IResponseMessage>(APIConstants.register, registerData);
  }

  // getCurrentUserPermission():  Observable<IPermissionReponseAPI>{
  //   return this.http.get<IPermissionReponseAPI>(APIConstants.userPermission);
  // }

  forgotPassword(forgotPasswordData: IForgotPasswordData): Observable<IResponseMessage> {
    return this.http.post<IResponseMessage>(APIConstants.forgotPassword, forgotPasswordData);
  }

  verificationCode(verificationCodeData: IVerficationCodeData): Observable<IResponseMessage> {
    return this.http.post<IResponseMessage>(APIConstants.verificationCode, verificationCodeData);
  }

  resetPassword(resetPasswordData: IResetPasswordData): Observable<IResponseMessage> {
    return this.http.post<IResponseMessage>(APIConstants.resetPassword, resetPasswordData);
  }

  changePassword(changePasswordData: IChangePasswordData): Observable<IResponseMessage> {
    return this.http.post<IResponseMessage>(APIConstants.changePassword, changePasswordData);
  }

  emailVerification(): Observable<IResponseMessage> {
    return this.http.post<IResponseMessage>(APIConstants.emailVerification, null);
  }

  emailVerificationCheck(verificationCode: string): Observable<IResponseMessage> {
    return this.http.post<IResponseMessage>(APIConstants.emailVerificationCheck, verificationCode);
  }

  generateToken(): Observable<{ token: string }> {
    return this.http.get<{ token: string }>(APIConstants.generateJwtToken);
  }

  accountVerifyPassword(accountDeleteData: IAccountDeleteData): Observable<IResponseMessage> {
    return this.http.post<IResponseMessage>(APIConstants.accountVerifyPassword, accountDeleteData);
  }

  accountVerifyCode(accountDeleteData: IAccountDeleteData): Observable<IResponseMessage> {
    return this.http.post<IResponseMessage>(APIConstants.accountVerifyCode, accountDeleteData);
  }

  accountDelete(): Observable<IResponseMessage> {
    return this.http.delete<IResponseMessage>(APIConstants.accountDelete);
  }

  validateImpersonateJwtToken(jwtToken: string) {
    return this.http.post<{ user_token: string }>(APIConstants.validateImpersonateJwtToken, {
      token: jwtToken
    });
  }
}

export const canActivateSignage: CanActivateFn = () => {
  return inject(AuthService).canActivate();
};

export const canActivateLoginPage: CanActivateFn = () => {
  return inject(AuthService).canActivateLogin();
};

export const canActivatePermission: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  return inject(AuthService).canActivatePermission(route);
};

export const canActivateLauncher: CanActivateFn = () => {
  return inject(AuthService).canActivateLauncher();
};

